import type { AuditLog } from "@wingspanhq/wingspan-api-sdk";
import { formatActorIdentity, formatActorLabel } from "./formatActor";
import { EventGroup, GROUP_WINDOW_MS } from "./types";

const sortByTimestampDescending = (events: AuditLog[]): AuditLog[] =>
  [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

/**
 * v3 carries no machine event type: `title` is the derived human string and the
 * only thing that separates one kind of entry from another, so it takes the
 * place of the removed `eventType` in the grouping key.
 */
const getGroupingKey = (event: AuditLog): string =>
  `${formatActorIdentity(event)}::${event.title}`;

const isWithinGroupWindow = (
  anchorTime: number,
  candidateTime: number
): boolean => Math.abs(anchorTime - candidateTime) <= GROUP_WINDOW_MS;

const toEventGroup = (events: AuditLog[]): EventGroup => {
  const anchor = events[0];

  if (events.length === 1) {
    return { type: "single", event: anchor };
  }

  return {
    type: "grouped",
    events,
    actor: formatActorLabel(anchor),
    eventTitle: anchor.title,
    anchorTimestamp: anchor.timestamp
  };
};

/**
 * Groups consecutive events by actor, title, and time window.
 * Events are grouped only when they are adjacent (no other events in-between),
 * share the same actor and title, and occur within 30 minutes of the anchor.
 */
export function groupEvents(events: AuditLog[]): EventGroup[] {
  if (events.length === 0) return [];

  const sorted = sortByTimestampDescending(events);
  const groups: EventGroup[] = [];
  let i = 0;

  while (i < sorted.length) {
    const anchor = sorted[i];

    // An entry with nothing to group *on* is never grouped, so a batch of
    // untitled entries cannot collapse into one line.
    if (!anchor.title) {
      groups.push({ type: "single", event: anchor });
      i++;
      continue;
    }

    const anchorKey = getGroupingKey(anchor);
    const anchorTime = anchor.timestamp.getTime();
    const groupedEvents = [anchor];

    let j = i + 1;
    while (j < sorted.length) {
      const candidate = sorted[j];
      if (
        getGroupingKey(candidate) === anchorKey &&
        isWithinGroupWindow(anchorTime, candidate.timestamp.getTime())
      ) {
        groupedEvents.push(candidate);
        j++;
      } else {
        break;
      }
    }

    groups.push(toEventGroup(groupedEvents));
    i = j;
  }

  return groups;
}
